import { useState } from 'react';
import { Link } from 'react-router-dom';
import JsonLd from '@/components/base/JsonLd';

const SITE_URL = 'https://oopbuyspread.hair';

const faqs = [
  {
    q: 'Was ist OOPBuy Spreadsheet?',
    a: 'OOPBuy Spreadsheet ist eine strukturierte Übersichtsplattform, die Nutzern hilft, Produkte schneller zu entdecken, zu vergleichen und zu organisieren. Es bündelt verschiedene Produktkategorien in einer zentralen, übersichtlichen Darstellung und ermöglicht so einen effizienten Einkaufsprozess. Die Plattform richtet sich an alle, die Wert auf strukturierte Informationen und schnelle Produktvergleiche legen.',
  },
  {
    q: 'Wie funktioniert OOPBuy Spreadsheet?',
    a: 'Das OOPBuy Spreadsheet funktioniert in fünf einfachen Schritten: Öffne das Spreadsheet, wähle eine Kategorie aus, vergleiche die verfügbaren Produkte, prüfe alle relevanten Informationen und wechsle dann zum Hauptportal für den Kauf. Jede Kategorie ist klar strukturiert, sodass du dich schnell zurechtfindest.',
  },
  {
    q: 'Ist OOPBuy Spreadsheet kostenlos?',
    a: 'Die OOPBuy Spreadsheet Übersichtsplattform ist kostenlos zugänglich. Sie dient als Informationsquelle und bietet einen strukturierten Überblick über verschiedene Produktkategorien. Für den tatsächlichen Kauf von Produkten gelten die jeweiligen Preise auf der Hauptplattform.',
  },
  {
    q: 'Ist OOPBuy Spreadsheet sicher?',
    a: 'Ja, OOPBuy Spreadsheet ist sicher. Die Plattform speichert keine persönlichen Daten und arbeitet mit transparenten Strukturen. Es gelten die üblichen Sicherheitsempfehlungen: Quellen prüfen, auf korrekte Domains achten, sichere Passwörter verwenden und verantwortungsvoll mit persönlichen Informationen umgehen.',
  },
  {
    q: 'Welche Kategorien gibt es im OOPBuy Spreadsheet?',
    a: 'Das OOPBuy Spreadsheet umfasst 14 verschiedene Kategorien: Schuhe, Hoodies & Sweater, T-Shirts, Jacken, Hosen & Shorts, Kopfbedeckung, Sets, Unterwäsche, Trikots, Accessoires, Taschen, Gürtel, Parfums und Sonstiges. Jede Kategorie enthält detaillierte Produktinformationen und Vergleichsmöglichkeiten.',
  },
  {
    q: 'Wie finde ich die besten Produkte im OOPBuy Spreadsheet?',
    a: 'Um die besten Produkte zu finden, empfehlen wir, die Kategorien systematisch zu durchsuchen, Bewertungen zu vergleichen und die strukturierten Vergleichstabellen zu nutzen. Die Plattform ist so aufgebaut, dass du schnell zwischen verschiedenen Optionen navigieren und fundierte Entscheidungen treffen kannst.',
  },
  {
    q: 'Gibt es einen OOPBuy Spreadsheet Guide für Anfänger?',
    a: 'Ja, wir bieten einen umfassenden OOPBuy Spreadsheet Guide speziell für Anfänger. Der Guide erklärt Schritt für Schritt, wie du die Plattform optimal nutzt, welche Kategorien es gibt und wie du die besten Produkte für deine Bedürfnisse findest. Besuche unsere Guide-Seite für detaillierte Informationen.',
  },
  {
    q: 'Werden die Informationen im OOPBuy Spreadsheet regelmäßig aktualisiert?',
    a: 'Die Informationen im OOPBuy Spreadsheet werden regelmäßig gepflegt und aktualisiert, um Nutzern stets aktuelle und relevante Daten zu bieten. Produktverfügbarkeiten, Preise und Kategorien werden kontinuierlich überprüft, damit du immer auf dem neuesten Stand bist.',
  },
  {
    q: 'Kann ich OOPBuy Spreadsheet auf dem Handy nutzen?',
    a: 'Ja, OOPBuy Spreadsheet ist vollständig responsiv und kann auf allen Geräten genutzt werden – vom Desktop-Computer über Tablets bis hin zu Smartphones. Die mobile Ansicht ist optimiert für eine einfache Navigation und schnelle Produktsuche unterwegs.',
  },
  {
    q: 'Gibt es Alternativen zum OOPBuy Spreadsheet?',
    a: 'Es gibt verschiedene Ansätze zur Produktrecherche, aber OOPBuy Spreadsheet zeichnet sich durch seine strukturierte Übersicht und benutzerfreundliche Kategorisierung aus. Die Kombination aus detaillierten Informationen, Vergleichsmöglichkeiten und direkter Verlinkung zum Hauptportal macht es zu einer effizienten Lösung.',
  },
  {
    q: 'Wie unterscheidet sich OOPBuy Spreadsheet von anderen Plattformen?',
    a: 'OOPBuy Spreadsheet hebt sich durch seine speziell strukturierte Darstellung ab. Statt unübersichtlicher Produktlisten bietet es kategorisierte Übersichten mit direkten Vergleichsmöglichkeiten. Der Fokus liegt auf Benutzerfreundlichkeit, schneller Navigation und fundierten Kaufentscheidungen.',
  },
  {
    q: 'Kann ich eigene Kategorien im OOPBuy Spreadsheet erstellen?',
    a: 'Das OOPBuy Spreadsheet bietet 14 vordefinierte Kategorien, die die wichtigsten Produktbereiche abdecken. Die bestehenden Kategorien sind so umfassend gestaltet, dass die meisten Nutzer alle ihre Anforderungen abdecken können. Für spezielle Anfragen empfehlen wir die Kategorie Sonstiges.',
  },
];

export default function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.a,
      },
    })),
  };

  return (
    <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
      <JsonLd data={faqJsonLd} />
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-10 md:mb-14">
          <p className="text-xs md:text-sm font-medium text-accent-600 uppercase tracking-wider mb-2">
            FAQ
          </p>
          <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900">
            Häufig gestellte Fragen zum OOPBuy Spreadsheet
          </h2>
        </div>

        <div className="space-y-3">
          {faqs.map((faq, idx) => (
            <div
              key={idx}
              className="bg-white rounded-lg border border-background-200/70 overflow-hidden"
            >
              <button
                className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer hover:bg-background-50 transition-colors"
                onClick={() => setOpenIndex(openIndex === idx ? null : idx)}
              >
                <span className="text-sm md:text-base font-medium text-foreground-900 pr-4">
                  {faq.q}
                </span>
                <i
                  className={`ri-arrow-down-s-line text-foreground-400 transition-transform duration-200 flex-shrink-0 ${
                    openIndex === idx ? 'rotate-180' : ''
                  }`}
                ></i>
              </button>
              {openIndex === idx && (
                <div className="px-5 pb-4">
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {faq.a}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="text-center mt-10">
          <Link
            to="/artikel/oopbuy-spreadsheet-guide"
            className="inline-flex items-center gap-2 px-6 py-3 text-sm font-medium bg-foreground-900 text-background-50 rounded-full hover:bg-foreground-800 transition-all whitespace-nowrap cursor-pointer"
          >
            Alle Fragen im vollständigen Guide
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </div>
    </section>
  );
}